/**
 * Movement maths, kept pure so it can be tested without a browser.
 *
 * The controls that use this need a DOM, a pointer lock and a GPU. The
 * arithmetic does not, and the arithmetic is where the bugs are — so it lives
 * here on its own and has tests.
 */

import { lookVector, PLAYER_HEIGHT, PLAYER_RADIUS } from '@lofi/shared';

export interface MoveInput {
  readonly forward: boolean;
  readonly back: boolean;
  readonly left: boolean;
  readonly right: boolean;
  readonly up: boolean;
  readonly down: boolean;
  readonly run: boolean;
  /** 🤫 Sneaking — Z held, half a walk. Decision 134: how you get near a sleeping fox. */
  readonly sneak?: boolean;
}

export interface MoveResult {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const WALK_SPEED = 5.6; // m/s — brisk walking
export const RUN_SPEED = 12;
/**
 * 🤫 **Sneaking, m/s — half a walk.** Decision 134: a sleeping fox wakes if you
 * walk within 4 m or run within 12, and never if you sneak. ⚠️ The server reads
 * your speed from where you are, so this number IS the stealth — it must stay
 * under `WALKING_SPEED` in `foxmind.ts` (4.2).
 */
export const SNEAK_SPEED = WALK_SPEED / 2;
/* ⚠️ Was 28 m/s, which haugur reported as painfully slow once the world became
   1,440 m across. Crossing the test area is now the common case, not a rare
   one, and it should not be the slow part of testing. */
export const FLY_SPEED = 36;

export function speedFor(flying: boolean, run: boolean, sneak = false): number {
  if (flying) return run ? FLY_SPEED * 2.5 : FLY_SPEED;
  /* 🤫 Sneaking wins over running: holding both is creeping, not sprinting. */
  if (sneak) return SNEAK_SPEED;
  return run ? RUN_SPEED : WALK_SPEED;
}

/** How long you must hold a direction while flying before it speeds up. */
export const BOOST_AFTER_SECONDS = 4;
/** And how long the change takes once it starts. */
export const BOOST_RAMP_SECONDS = 6;
/**
 * How much faster it ends up.
 *
 * ⚠️ Doubling was the original request and it was not enough — at 2× a crossing
 * of the test area is still most of half a minute. Four times, reached ten
 * seconds into a hold, crosses it in about ten seconds and still starts gently
 * enough to place yourself precisely.
 */
export const MAX_FLIGHT_BOOST = 4;

/**
 * Flying gets faster the longer you hold a direction.
 *
 * The test area is 1,440 m across and flying it at a constant 28 m/s is most of
 * a minute of holding W. Crossing it should not be the slow part of testing.
 *
 * ⚠️ Ramped over two seconds rather than switched at four. A step change to
 * double speed at the moment you are lining something up throws the camera past
 * it, which is worse than the wait it saves.
 */
export function flightBoost(heldSeconds: number): number {
  if (!Number.isFinite(heldSeconds) || heldSeconds <= BOOST_AFTER_SECONDS) return 1;
  const into = Math.min(heldSeconds - BOOST_AFTER_SECONDS, BOOST_RAMP_SECONDS);
  return 1 + (MAX_FLIGHT_BOOST - 1) * (into / BOOST_RAMP_SECONDS);
}

/**
 * Desired velocity, in metres per second, from the keys held and where the
 * player is facing.
 *
 * Horizontal movement is normalised so walking diagonally is not faster than
 * walking straight — the classic bug where WD is 1.41× as fast as W.
 *
 * ⚠️ Vertical is applied **after** normalisation, deliberately. Normalising a
 * combined 3D vector would make flying forward-and-up slower than flying
 * forward, which feels broken even though the maths is defensible.
 */
export function computeVelocity(input: MoveInput, yaw: number, flying: boolean): MoveResult {
  const forwardX = -Math.sin(yaw);
  const forwardZ = -Math.cos(yaw);
  const rightX = Math.cos(yaw);
  const rightZ = -Math.sin(yaw);

  let x = 0;
  let z = 0;
  if (input.forward) {
    x += forwardX;
    z += forwardZ;
  }
  if (input.back) {
    x -= forwardX;
    z -= forwardZ;
  }
  if (input.right) {
    x += rightX;
    z += rightZ;
  }
  if (input.left) {
    x -= rightX;
    z -= rightZ;
  }

  const speed = speedFor(flying, input.run, input.sneak ?? false);

  const length = Math.hypot(x, z);
  if (length > 0) {
    x = (x / length) * speed;
    z = (z / length) * speed;
  }

  let y = 0;
  if (flying) {
    if (input.up) y += speed;
    if (input.down) y -= speed;
  }

  return { x, y, z };
}

/**
 * How far the view turns per pixel of mouse movement, before the player's own
 * scale is applied.
 *
 * ⚠️ Was 0.0022, which haugur reported as unusably fast: a 1,400-pixel sweep
 * across the screen turned you 180°, so any ordinary flick of the wrist span
 * the world. At this value the same sweep is a comfortable ~100°.
 */
export const LOOK_RADIANS_PER_PIXEL = 0.0012;

/** User-adjustable multiplier on top of it. 1 is the default feel. */
export const MIN_LOOK_SCALE = 0.25;
export const MAX_LOOK_SCALE = 3;
export const LOOK_SCALE_STEP = 0.25;

export function clampLookScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  return Math.max(MIN_LOOK_SCALE, Math.min(MAX_LOOK_SCALE, scale));
}

/**
 * ⚠️ Largest single mouse event we will believe, in pixels.
 *
 * Pointer lock reports `movementX/Y` straight from the OS, and it is not
 * bounded. Remote desktop, a display switch, an accelerated mouse driver or the
 * browser re-acquiring the pointer can all deliver one event of several hundred
 * pixels. Unclamped, that is a whole-turn jump in a single frame — which reads
 * as the view flipping rather than as the mouse being fast.
 *
 * 120 px is far more than a real hand produces in one 60 Hz frame, so genuine
 * movement passes through untouched and only the spikes are cut.
 */
export const MAX_LOOK_DELTA_PX = 120;

/** Radians to turn for one mouse event, spike-clamped and scaled. */
export function lookDelta(movementPx: number, scale: number): number {
  if (!Number.isFinite(movementPx)) return 0;
  const px = Math.max(-MAX_LOOK_DELTA_PX, Math.min(MAX_LOOK_DELTA_PX, movementPx));
  return px * LOOK_RADIANS_PER_PIXEL * clampLookScale(scale);
}

/**
 * How close to straight up or down the view may get.
 *
 * ⚠️ Was 0.01 rad (0.6°). That is enough to stop the Euler angles degenerating,
 * but not enough to stop the *zoomed-out* camera behaving badly: at 0.6° off
 * vertical the pull-back is essentially straight down, so the camera dives
 * under the ground. 2° leaves a margin you cannot see but the camera can use.
 */
export const PITCH_LIMIT = Math.PI / 2 - 0.035;

/** Clamp pitch just short of vertical. */
export function clampPitch(pitch: number): number {
  /* Exactly ±90° makes the view direction parallel to the up vector, and the
     camera flips over. */
  return Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
}

/**
 * How much further the mouse must be shoved up, once the view has stopped
 * rising, before the game gives the cursor back so the status bar can be used.
 *
 * ⚠️ **A judgement, and the number to change if this misbehaves.** 400 px of raw
 * movement is a firm push rather than a flick — well past the point where the
 * picture visibly stopped responding, so the intent is unambiguous. **Too small
 * and looking at the sky ends your game; too large and nobody discovers the bar
 * exists.**
 */
export const OVERSHOOT_TO_RELEASE = 400;

/**
 * Accumulate the shove that reaches for the status bar (2026-08-11).
 *
 * 🔴 **The problem.** While the pointer is locked the cursor does not exist —
 * the browser reports *movement* and nothing else — so the bar cannot be clicked
 * or even pointed at while you are playing. haugur asked for the mouse reaching
 * the top of the screen to hand the cursor back.
 *
 * ⚠️ **I flagged this as the risky option and he chose it**, so it is built to be
 * hard to trigger by accident. The naive version — release on upward movement —
 * fires every time you look at the sky, which is the single most common thing a
 * person does in a world that now has weather in it.
 *
 * **Two gates, and both must hold:**
 *
 * 1. **The view is already as far up as it goes.** Below the pitch limit,
 *    upward movement is *looking up* and means nothing else.
 * 2. **You keep pushing** past the point where the picture stopped responding.
 *
 * 🔴 **Any downward movement resets it to zero.** The overshoot has to be one
 * deliberate shove, not an amount accumulated across a session — otherwise the
 * release arrives minutes later attached to nothing the player did, which is the
 * worst possible version of this.
 *
 * @param current   the overshoot so far, in pixels
 * @param movementY the browser's `movementY`; **negative is up**
 * @param pitch     the pitch AFTER this movement was applied
 * @returns the new overshoot. At or above {@link OVERSHOOT_TO_RELEASE}, release.
 */
export function reachOvershoot(current: number, movementY: number, pitch: number): number {
  /* Moving down at all: not reaching for anything. */
  if (movementY >= 0) return 0;
  /* Still able to look further up, so this is looking up. */
  if (pitch < PITCH_LIMIT - 1e-6) return 0;
  return current - movementY;
}

/** How far the camera sits behind the player. 0 is first person. */
export const MIN_ZOOM = 0;
/* 60 m was not nearly enough to take in a town. At 400 m the whole of central
   Hafnarfjörður fits on screen. */
export const MAX_ZOOM = 400;

export function clampZoom(distance: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, distance));
}

/**
 * Where the camera goes, given the player's eye position and how far back the
 * view is zoomed.
 *
 * Pulling straight back along the view direction, so the player stays in the
 * centre of the screen at any zoom.
 */
export function cameraOffset(yaw: number, pitch: number, distance: number): MoveResult {
  if (distance <= 0) return { x: 0, y: 0, z: 0 };

  const cosPitch = Math.cos(pitch);
  return {
    x: Math.sin(yaw) * cosPitch * distance,
    y: -Math.sin(pitch) * distance,
    z: Math.cos(yaw) * cosPitch * distance,
  };
}

/**
 * Which way the player is looking — a unit vector (T52).
 *
 * ⚠️ **The exact opposite of {@link cameraOffset}, and that is easy to get
 * wrong.** `cameraOffset` points from the player *to the camera*, which sits
 * *behind* them; the view direction is the other way. Deriving one from the
 * other by negation, here, means they cannot drift apart — a look direction
 * that is backwards would make you break the block behind your head, which
 * looks like broken aiming rather than a sign error.
 */
export function lookDirection(yaw: number, pitch: number): MoveResult {
  /* 🔴 **The arithmetic MOVED to `@lofi/shared`'s `lookVector` on 2026-09-22
     (T221), and this now calls it.** The server has to answer the same question
     — *was that fish in front of the player* — and a second copy of a sign
     convention is how `fishshot.mjs` came to photograph every fish nose-on and
     invent a thin þorskur out of nothing. ⚠️ **The negation of `cameraOffset`
     is still the reason it is what it is**, and the note above still explains
     it; what is gone is this file being the only place that knows. */
  return lookVector(yaw, pitch);
}

/* ── walls ────────────────────────────────────────────────────────────────── */

/* 🧍 **The player's size lives in `@lofi/shared` now** (`crowd.ts`, T274) —
   the server needs it too, to know when you are pressing a sheep. */
export { PLAYER_HEIGHT, PLAYER_RADIUS };

/**
 * How much of a cell is solid, 0…1 — the same shape `controls.ts` already uses.
 */
export interface SolidAt {
  (x: number, y: number, z: number): number;
}

/**
 * The highest solid surface in one column between two heights, or `null`.
 *
 * ⚠️ **Heights, not booleans.** A quarter-height hearth has its surface at
 * `by + 0.25`, and treating it as a whole metre is the fault T160 fixed.
 */
function highestTopBetween(
  isSolid: SolidAt,
  bx: number,
  bz: number,
  fromY: number,
  toY: number,
): number | null {
  let best: number | null = null;
  /* 🧱 From ONE CELL LOWER: a 1.5 m stone wall (D130) reaches up into the
     cell above its own, so a span that starts there must still see it. The
     `top > fromY` test below discards every block that does not reach. */
  for (let by = Math.floor(fromY) - 1; by <= Math.floor(toY); by++) {
    const height = isSolid(bx, by, bz);
    if (height <= 0) continue;
    const top = by + height;
    if (top > fromY && top <= toY && (best === null || top > best)) best = top;
  }
  return best;
}

/** Is any part of this column solid between two heights? */
function anySolidBetween(
  isSolid: SolidAt,
  bx: number,
  bz: number,
  fromY: number,
  toY: number,
): boolean {
  /* 🧱 From ONE CELL LOWER: a 1.5 m stone wall (D130) reaches up into the
     cell above its own, so a span that starts there must still see it. The
     `top > fromY` test below discards every block that does not reach. */
  for (let by = Math.floor(fromY) - 1; by <= Math.floor(toY); by++) {
    const height = isSolid(bx, by, bz);
    if (height <= 0) continue;
    const top = by + height;
    /* The block's solid part is `by … top`. It counts if that overlaps the
       span at all — a block whose solid quarter ends below `fromY` does not. */
    if (top > fromY && by < toY) return true;
  }
  return false;
}

/**
 * 🧱 **Would the player's body fit standing here?** `false` means blocked.
 *
 * 🔴 **THE BUG THIS EXISTS TO FIX — haugur, 2026-09-01:** *"if i walk towards a
 * tree and right up to it, instead of beeing blocked i just go to the top of the
 * tree… i can basically walk through things, just transported to the top of
 * world."*
 *
 * 🔴 **The cause was that there was no horizontal collision AT ALL**, and
 * `controls.ts` said so in as many words. Movement resolved the ground and
 * nothing else: `groundBelow` scans DOWN from your feet plus a step, so walking
 * into a trunk found the trunk block you were now standing inside and lifted you
 * onto it — **every frame**, which is the ride to the top of the world.
 *
 * ## ⚠️ The rule, and why it is NOT simply "is there a block there"
 *
 * A step must stay walkable: haugur has built a stairway and it should not
 * suddenly need jumping. **So the test is not "is the cell solid" but "does the
 * player FIT above whatever they would step onto"**:
 *
 * 1. Find the highest surface in the column within one step of the feet.
 * 2. Ask whether the body's full height is clear above *that* surface.
 *
 * 👉 **A tree trunk fails at step 2 and a stair passes it.** Stepping onto the
 * first trunk block would put your body inside the second and third, so you are
 * blocked; stepping onto a stair puts you in open air, so you are not.
 *
 * ⚠️ **This is why STEP_HEIGHT did not have to change.** The obvious fix was to
 * drop it below a metre so a full block could not be stepped up — and that would
 * have made every full-block staircase in the world need a jump, which is a
 * gameplay change nobody asked for.
 *
 * @param feetY where the feet are now — the step is measured from here
 */
/**
 * 🦶 **The block columns the player's feet cover.**
 *
 * ⚠️ **Deduplicated**, because at most positions the four corners land in the
 * same one or two cells, and testing a column twice is wasted work in something
 * called every frame.
 *
 * 🔴 **One copy, shared by {@link bodyFitsAt} and {@link fitsAtHeight}.** The
 * two ask different questions of the same footprint, and a second copy of *what
 * the footprint is* is the fault this repo names oftener than any other.
 */
function footprintCells(x: number, z: number): [number, number][] {
  const r = PLAYER_RADIUS;
  const cells = new Set<string>();
  for (const cx of [x - r, x + r]) {
    for (const cz of [z - r, z + r]) {
      cells.add(`${Math.floor(cx)},${Math.floor(cz)}`);
    }
  }
  return [...cells].map((cell) => cell.split(',').map(Number) as [number, number]);
}

/**
 * ⬆️ **Does the player fit standing exactly here, with NO step-up allowance?**
 *
 * 🔴 **THE DIFFERENCE FROM {@link bodyFitsAt} IS THE WHOLE POINT.** That one
 * asks *"could you walk to this column"*, and to answer it generously it looks
 * for something to step onto up to `STEP_HEIGHT` above your feet. **This one
 * asks *"is this exact height free"***, which is what a jump needs: rising into
 * a tunnel roof must be refused, not resolved by standing you on the roof.
 *
 * 🎯 **Reported from inside the game, 2026-09-09** — *"Jumping underground
 * spawns you overground"* (#561), by the first player who was not haugur.
 */
export function fitsAtHeight(isSolid: SolidAt, x: number, feetY: number, z: number): boolean {
  for (const [bx, bz] of footprintCells(x, z)) {
    /* ⚠️ A hair above the feet, so the block you STAND on is not counted as
       being inside you — the same 0.02 `bodyFitsAt` uses, and for the same
       reason. */
    if (anySolidBetween(isSolid, bx, bz, feetY + 0.02, feetY + PLAYER_HEIGHT)) return false;
  }
  return true;
}

export function bodyFitsAt(
  isSolid: SolidAt,
  x: number,
  feetY: number,
  z: number,
  stepHeight: number,
): boolean {
  for (const [bx, bz] of footprintCells(x, z)) {
    /* What you would end up standing on here: the ground itself, or a step up
       to something no higher than a kerb. */
    const step = highestTopBetween(isSolid, bx, bz, feetY, feetY + stepHeight);
    const standOn = step ?? feetY;
    /* ⚠️ A hair above the surface, so the block you are standing ON does not
       count as being inside you. */
    if (anySolidBetween(isSolid, bx, bz, standOn + 0.02, standOn + PLAYER_HEIGHT)) {
      return false;
    }
  }
  return true;
}

/**
 * 🪜 **How fast the eye catches up after you step up, in metres per second.**
 *
 * 🎯 **haugur, 2026-09-09**, on the stairs beside his campfire: *"when i walk
 * down them all is fine but when i walk up it like my character is not moving
 * up but the whole world is movin down, especially noticable if i walk
 * backwards up the stairs. it look funky lol"*
 *
 * 🔴 **He was describing a ONE-FRAME TELEPORT.** Stepping up sets the feet to
 * the new ground outright, so the camera nailed to them rose a whole metre in
 * 16 ms. **Walking DOWN was always fine because gravity spreads a fall over
 * many frames** — which is exactly the asymmetry he noticed.
 *
 * ⚠️ **This is a case where copying Minecraft would be WRONG.** Vanilla does
 * not smooth stair steps at all; it is a long-standing feature request, and it
 * gets away with it because **its stairs are half-blocks.** Ours is a tunnel cut
 * in whole 1 m voxels, so every step is twice the jolt.
 *
 * **8 m/s puts a full 1 m step at about 130 ms** — long enough to read as
 * climbing, short enough that the eye is never meaningfully behind the body.
 */
export const STEP_CATCHUP_SPEED = 8;

/**
 * 🪜 **How far behind the eye falls when the feet rise**, in metres.
 *
 * 🔴 **Only a rise while ALREADY STANDING is a step.** Coming down onto the
 * ground after a fall or a jump goes through the same code, and smoothing that
 * would make every landing float — the fall was already spread over many frames
 * by gravity and needs no help at all.
 *
 * ⚠️ **Clamped.** A player put somewhere by a spawn, a teleport, or a chunk
 * arriving late can rise any distance, and an unclamped lag would leave the eye
 * tens of metres underground looking up through the world.
 *
 * @param lag what the eye is already behind by.
 * @param rise how far the feet just went up. **Zero or negative is not a step.**
 * @param wasOnGround were the feet already on something before this frame?
 * @param maxLag the tallest step that can be taken, so the clamp matches it.
 */
export function stepLagAfterRise(
  lag: number,
  rise: number,
  wasOnGround: boolean,
  maxLag: number,
): number {
  if (!wasOnGround || rise <= 0) return lag;
  return Math.min(maxLag, lag + rise);
}

/**
 * 🪜 **The lag one frame later** — it closes at a fixed SPEED, so a tall step
 * takes longer to catch up than a shallow one, which is what climbing looks
 * like. A fixed *duration* would make a kerb feel as slow as a full block.
 */
export function stepLagDecayed(lag: number, dt: number): number {
  return Math.max(0, lag - STEP_CATCHUP_SPEED * dt);
}
